import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { XIcon, ChatIcon } from "@heroicons/react/solid";
import {
  parseActionsFromContent,
  parseShowFromContent,
  handleAction,
  detectActionsFromQuestion,
  detectShowFromQuestion,
  showOnPage,
} from "./utils";
import { PageSpotlight } from "./PageSpotlight";

// ChatWindow (and its dependency on marked/dompurify for markdown rendering)
// only loads once the chat is actually opened, instead of shipping on every
// page load for a feature most visitors never use.
const ChatWindow = lazy(() =>
  import("./ChatWindow").then((module) => ({ default: module.ChatWindow }))
);
import type { Message } from "./types";
import { generateMessageId } from "./types";
import { useLocalStorage } from "../../hooks";
import {
  trackChatOpened,
  trackMessageSent,
  trackResponseReceived,
  trackSuggestionClicked,
  trackQuickActionClicked,
  trackActionTriggered,
  trackChatError,
  trackChatCleared,
  trackChatSessionEnd,
} from "../../utils/aiChatAnalytics";

const INITIAL_MESSAGE: Message = {
  id: generateMessageId(),
  role: "assistant",
  content: "Hi! Ask me about Andrew's work, projects, or background.",
  timestamp: Date.now(),
};

const LABEL_SEEN_KEY = "ai-chat-label-shown";
const LABEL_DELAY_MS = 10000;
const LABEL_VISIBLE_MS = 8000;
const LABEL_FADE_MS = 400; // matches ai-label-fade-out

// sessionStorage throws in some privacy modes; the label then just shows again.
const hasSeenLabel = () => {
  try {
    return sessionStorage.getItem(LABEL_SEEN_KEY) === "true";
  } catch {
    return false;
  }
};

const markLabelSeen = () => {
  try {
    sessionStorage.setItem(LABEL_SEEN_KEY, "true");
  } catch {
    // ignore
  }
};

export const AIChatAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useLocalStorage<Message[]>("ai-chat-history", [
    INITIAL_MESSAGE,
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [labelPhase, setLabelPhase] = useState<"idle" | "shown" | "hiding" | "done">(
    () => (hasSeenLabel() ? "done" : "idle")
  );
  const [isEngaged, setIsEngaged] = useState(false);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const sessionStartTimeRef = useRef<number | null>(null);
  const isMac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent);
  const shortcutKey = isMac ? "⌘ + D" : "Ctrl + D";
  const [shouldHideMobileToggle, setShouldHideMobileToggle] = useState(false);

  // On narrow viewports, the fixed bottom-right chat toggle can overlap the
  // hero's own CTAs. Hide it only for that initial stretch; desktop keeps
  // the always-visible affordance.
  useEffect(() => {
    const checkPosition = () => {
      const isMobile = window.innerWidth < 640;
      setShouldHideMobileToggle(isMobile && window.scrollY < 700);
    };
    checkPosition();
    window.addEventListener("scroll", checkPosition, { passive: true });
    window.addEventListener("resize", checkPosition);
    return () => {
      window.removeEventListener("scroll", checkPosition);
      window.removeEventListener("resize", checkPosition);
    };
  }, []);

  // Track session start time when chat opens
  useEffect(() => {
    if (isOpen && !sessionStartTimeRef.current) {
      sessionStartTimeRef.current = Date.now();
    }
  }, [isOpen]);

  // The once-per-session label waits for engagement (10s on the site or a
  // half-screen scroll) so it doesn't compete with the hero headline.
  useEffect(() => {
    if (labelPhase !== "idle" || isEngaged) return;
    const engage = () => setIsEngaged(true);
    const onScroll = () => {
      if (window.scrollY > window.innerHeight / 2) engage();
    };
    const timer = setTimeout(engage, LABEL_DELAY_MS);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [labelPhase, isEngaged]);

  // Show only once the toggle is actually visible (mobile hides it near the
  // top), and drop the label as soon as the chat opens.
  useEffect(() => {
    if (isOpen && (labelPhase === "shown" || labelPhase === "hiding")) {
      setLabelPhase("done");
    } else if (labelPhase === "idle" && isEngaged && !isOpen && !shouldHideMobileToggle) {
      setLabelPhase("shown");
      markLabelSeen();
    }
  }, [isOpen, labelPhase, isEngaged, shouldHideMobileToggle]);

  useEffect(() => {
    if (labelPhase !== "shown" && labelPhase !== "hiding") return;
    const timer = setTimeout(
      () => setLabelPhase(labelPhase === "shown" ? "hiding" : "done"),
      labelPhase === "shown" ? LABEL_VISIBLE_MS : LABEL_FADE_MS
    );
    return () => clearTimeout(timer);
  }, [labelPhase]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "d") {
        e.preventDefault();
        setIsOpen((prev) => {
          const newState = !prev;
          if (newState) {
            // Track when opened via keyboard
            const userMessages = messages.filter((m) => m.role === "user").length;
            trackChatOpened(userMessages === 0, userMessages);
          }
          return newState;
        });
      }
      if (e.key === "Escape" && isOpen) {
        const target = e.target as HTMLElement;
        const isInputOrTextarea =
          target.tagName === "INPUT" || target.tagName === "TEXTAREA";
        if (!isInputOrTextarea || !input.trim()) {
          e.preventDefault();
          handleClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, input, messages]);

  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
      // Track when opened via custom event
      const userMessages = messages.filter((m) => m.role === "user").length;
      trackChatOpened(userMessages === 0, userMessages);
    };
    window.addEventListener("openAIChat", handleOpenChat);
    return () => window.removeEventListener("openAIChat", handleOpenChat);
  }, [messages]);

  const handleClose = useCallback(() => {
    // Track session end metrics
    if (sessionStartTimeRef.current) {
      const sessionDuration = Date.now() - sessionStartTimeRef.current;
      const userMessages = messages.filter((m) => m.role === "user").length;
      trackChatSessionEnd(messages.length, sessionDuration, userMessages);
      sessionStartTimeRef.current = null;
    }
    setIsOpen(false);
    if (input) setInput("");
    // Return focus to the toggle so keyboard users aren't dropped on <body>
    toggleButtonRef.current?.focus({ preventScroll: true });
  }, [messages, input]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      const newState = !prev;
      if (newState) {
        // Opening - track it
        const userMessages = messages.filter((m) => m.role === "user").length;
        trackChatOpened(userMessages === 0, userMessages);
      } else {
        // Closing - track session end
        if (sessionStartTimeRef.current) {
          const sessionDuration = Date.now() - sessionStartTimeRef.current;
          const userMessages = messages.filter((m) => m.role === "user").length;
          trackChatSessionEnd(messages.length, sessionDuration, userMessages);
          sessionStartTimeRef.current = null;
        }
      }
      return newState;
    });
  }, [messages]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = messageText.trim();
    const userMessageCount =
      messages.filter((m) => m.role === "user").length + 1;

    // Track message sent
    trackMessageSent(userMessage.length, userMessageCount);

    setInput("");
    setMessages((prev) => [
      ...prev,
      {
        id: generateMessageId(),
        role: "user",
        content: userMessage,
        timestamp: Date.now(),
      },
    ]);
    setIsLoading(true);

    const requestStartTime = Date.now();

    try {
      const response = await fetch(
        "https://portfolio-ai-chat.andrew-portfolio-chat.workers.dev/api/chat",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: userMessage, messages }),
        }
      );

      const responseTime = Date.now() - requestStartTime;

      if (!response.ok) {
        trackChatError("api_error");
        throw new Error("Failed to get response");
      }

      const data = await response.json();
      const { cleanContent: withoutActions, actions: parsedActions } = parseActionsFromContent(data.response);
      const { cleanContent, show: taggedShow } = parseShowFromContent(withoutActions);
      // The model's own pick may auto-spotlight; a keyword guess only offers the button
      const show = taggedShow ?? detectShowFromQuestion(userMessage);
      
      // Use parsed actions from AI, or detect from question as fallback
      const actions = parsedActions.length > 0 
        ? parsedActions 
        : detectActionsFromQuestion(userMessage);

      // Track successful response
      trackResponseReceived(true, responseTime, (actions?.length ?? 0) > 0);

      const messageId = generateMessageId();
      setMessages((prev) => [
        ...prev,
        {
          id: messageId,
          role: "assistant",
          content: cleanContent,
          actions,
          show,
          timestamp: Date.now(),
          isStreaming: true,
        },
      ]);

      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, isStreaming: false } : msg
          )
        );
        // Desktop only: on mobile the chat covers the page, so the button does it
        if (
          taggedShow &&
          window.matchMedia("(min-width: 768px)").matches &&
          document.querySelector(`[data-chat-target="${taggedShow}"]`)
        ) {
          showOnPage(taggedShow);
        }
      }, cleanContent.split(/(\s+)/).length * 20 + 100);
    } catch (error) {
      trackChatError("network_error");
      setMessages((prev) => [
        ...prev,
        {
          id: generateMessageId(),
          role: "assistant",
          content:
            "Sorry, I'm having trouble connecting right now. Please try again later or reach out directly via the contact form!",
          error: true,
          timestamp: Date.now(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => sendMessage(input);

  const handleSuggestedQuestion = useCallback(
    (question: string, index: number, total: number) => {
      trackSuggestionClicked(index, total);
      sendMessage(question);
    },
    []
  );

  const handleClearChat = useCallback(() => {
    trackChatCleared(messages.length);
    setMessages([INITIAL_MESSAGE]);
    setShowSuggestions(true);
    setInput("");
  }, [messages.length, setMessages]);

  const handleRetry = useCallback(() => {
    const lastUserMessage = [...messages]
      .reverse()
      .find((msg) => msg.role === "user");
    if (lastUserMessage) {
      setMessages((prev) => prev.slice(0, -1));
      sendMessage(lastUserMessage.content);
    }
  }, [messages]);

  const handleActionClick = useCallback(
    (action: string) => {
      trackActionTriggered(action);

      if (action === "ask_directly") {
        const lastUserMessage = [...messages]
          .reverse()
          .find((msg) => msg.role === "user");
        if (lastUserMessage) {
          // Contact applies the event if it's mounted; otherwise it reads
          // sessionStorage after handleAction navigates to /#contact
          sessionStorage.setItem("preFillMessage", lastUserMessage.content);
          window.dispatchEvent(
            new CustomEvent("preFillContactForm", {
              detail: { message: lastUserMessage.content },
            })
          );
        }
      }
      handleAction(action, handleClose);
    },
    [messages, handleClose]
  );

  const handleShow = useCallback(
    (target: string) => {
      trackActionTriggered(`show:${target}`);
      if (window.matchMedia("(min-width: 768px)").matches) {
        showOnPage(target, true);
      } else {
        handleClose();
        setTimeout(() => showOnPage(target, true), 250);
      }
    },
    [handleClose]
  );

  const handleQuickAction = useCallback(
    (action: string) => {
      trackQuickActionClicked(action);
      handleAction(action, handleClose);
    },
    [handleClose]
  );

  return (
    <>
      <style>{`@keyframes ai-glow-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(34, 211, 238, 0), 0 0 16px 4px rgba(34, 211, 238, 0.25), 0 0 32px 8px rgba(147, 51, 234, 0.15); } 50% { box-shadow: 0 0 0 6px rgba(34, 211, 238, 0.08), 0 0 24px 8px rgba(34, 211, 238, 0.35), 0 0 48px 16px rgba(147, 51, 234, 0.2); } } @keyframes ai-label-fade-in { from { opacity: 0; transform: translateX(6px); } to { opacity: 1; transform: translateX(0); } } @keyframes ai-label-fade-out { from { opacity: 1; transform: translateX(0); } to { opacity: 0; transform: translateX(6px); } } .ai-chat-btn-glow { animation: ai-glow-pulse 2.8s ease-in-out infinite; } .ai-chat-btn-glow:hover { animation: none; box-shadow: 0 0 0 0 rgba(34, 211, 238, 0), 0 0 28px 10px rgba(34, 211, 238, 0.4), 0 0 56px 20px rgba(147, 51, 234, 0.25); } .ai-label-auto-show { animation: ai-label-fade-in 0.35s ease forwards; } .ai-label-auto-hide { animation: ai-label-fade-out 0.4s ease forwards; }`}</style>
      <div
        className={`fixed bottom-6 right-4 z-50 transition-opacity duration-300 md:bottom-24 md:right-6 ${
          isOpen ? "md:block hidden" : "block"
        } ${
          !isOpen && shouldHideMobileToggle
            ? "opacity-0 pointer-events-none"
            : "opacity-100"
        }`}
      >
        <div
          className={`absolute right-full mr-3 top-1/2 -translate-y-1/2 pointer-events-none select-none ${
            labelPhase === "shown"
              ? "ai-label-auto-show"
              : labelPhase === "hiding"
                ? "ai-label-auto-hide"
                : "opacity-0"
          }`}
          aria-hidden="true"
        >
          <div className="flex items-center gap-1.5 bg-slate-900/95 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full border border-slate-700/60 shadow-lg shadow-black/30 whitespace-nowrap">
            <span
              className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0"
              style={{ animation: "ai-glow-pulse 2s ease-in-out infinite" }}
            />
            Ask me anything about Drew
          </div>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full border-4 border-transparent border-l-slate-700/60" />
        </div>
        <button
          ref={toggleButtonRef}
          onClick={handleToggle}
          className={`group relative p-3.5 md:p-4 rounded-full shadow-lg transition-all duration-300 focus-ring focus:ring-offset-2 focus:ring-offset-slate-900 ${
            isOpen
              ? "bg-slate-700 hover:bg-slate-600"
              : "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 hover:scale-110 ai-chat-btn-glow"
          }`}
          aria-label={isOpen ? "Close chat" : "Open AI chat assistant"}
          aria-expanded={isOpen}
        >
          {isOpen ? (
            <XIcon className="w-6 h-6 text-white" />
          ) : (
            <ChatIcon className="w-6 h-6 text-white" />
          )}
          {!isOpen && (
            <div
              className="absolute right-full mr-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
              role="tooltip"
            >
              <div className="flex items-center gap-2 bg-slate-900/95 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full border border-slate-700/60 shadow-lg shadow-black/30 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                Ask me anything about Drew
                <kbd className="ml-1 px-1.5 py-0.5 bg-slate-700 rounded font-mono text-slate-300 text-[10px]">
                  {shortcutKey}
                </kbd>
              </div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full border-4 border-transparent border-l-slate-700/60" />
            </div>
          )}
        </button>
      </div>
      {isOpen && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center border border-slate-700 bg-slate-800 shadow-2xl md:inset-auto md:bottom-44 md:right-6 md:h-[min(85vh,680px)] md:w-96 md:max-w-[calc(100vw-3rem)] md:rounded-lg">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-500" />
            </div>
          }
        >
          <ChatWindow
            messages={messages}
            input={input}
            isLoading={isLoading}
            showSuggestions={showSuggestions}
            toggleButtonRef={toggleButtonRef}
            onClose={handleClose}
            onClear={handleClearChat}
            onInputChange={setInput}
            onSubmit={handleSubmit}
            onAction={handleActionClick}
            onShow={handleShow}
            onRetry={handleRetry}
            onSuggestedQuestion={handleSuggestedQuestion}
            onQuickAction={handleQuickAction}
          />
        </Suspense>
      )}
      <PageSpotlight />
    </>
  );
};
